import React, {useEffect, useState} from "react";
import {Prompt} from "react-router-dom";
import {Button, CircularProgress, IconButton, Typography} from "@mui/material";
import {NavigateBefore as PrevIcon, NavigateNext as NextIcon} from "@mui/icons-material";
import {VerticalDivider} from "@atttomyx/react-components";
import {TemplateForm} from "../forms";
import {templateService} from "../../services";
import {arrays, forms} from "@atttomyx/shared-utils";
import {verbiage} from "@atttomyx/shared-constants";
import {sortingUtils} from "../../utils";

const toData = (template) => {
    return {
        name: template.name,
        content: template.content,
        order: template.order,
        valid: template.name && template.content && sortingUtils.isSortable(template.order),
    }
};

const EditTemplatePage = (props) => {
    const { snackbar, match, templates, filters, submissionMergeFields, onSave, onPrev, onNext } = props;
    const [ template, setTemplate ] = useState({});
    const [ data, setData ] = useState(toData({}));
    const [ saving, setSaving ] = useState(false);
    const [ prevId, setPrevId ] = useState(null);
    const [ nextId, setNextId ] = useState(null);

    const loadTemplate = (templateId) => {
        const filtered = filters.filter(templates);
        const template = arrays.findEntity(filtered, templateId) || {};
        const ids = arrays.getPrevNextIds(filtered, template);

        setTemplate(template);
        setPrevId(ids.prevId);
        setNextId(ids.nextId);
    };

    const redirectToPrev = () => {
        onPrev(prevId);
        loadTemplate(prevId);
    };

    const redirectToNext = () => {
        onNext(nextId);
        loadTemplate(nextId);
    };

    useEffect(() => {
        const templateId = match.params.id;

        loadTemplate(templateId);
    }, []);

    useEffect(() => {
        setData(toData(template));
    }, [template]);

    const resetForm = () => {
        setData(toData(template));
    };

    const submitForm = () => {
        setSaving(true);

        const modified = {
            name: data.name,
            content: data.content,
            order: data.order,
        };

        const success = (template) => {
            setSaving(false);

            snackbar.setSuccess("Template saved");
            onSave(template);
            setTemplate(template);
        };

        const failure = (err) => {
            setSaving(false);

            snackbar.setError(err);
        };

        templateService.updateTemplate(template.id, modified, success, failure);
    }

    const valid = data.valid;
    const modified = forms.differ(toData(template), data);

    return saving ?
        <div className="edit-template-page">
            <CircularProgress size="80px"/>
        </div> :
        <div className="edit-template-page">
            <Typography variant="h5" paragraph={true}>
                Edit Template
            </Typography>
            <TemplateForm
                submissionMergeFields={submissionMergeFields}
                data={data}
                onChange={setData}
            />
            <div className="actions">
                <IconButton color="secondary" title="Previous template"
                            disabled={!prevId}
                            onClick={redirectToPrev}>
                    <PrevIcon/>
                </IconButton>
                <IconButton color="secondary" title="Next template"
                            disabled={!nextId}
                            onClick={redirectToNext}>
                    <NextIcon/>
                </IconButton>
                <VerticalDivider/>
                <Button color="secondary" variant="text"
                        disabled={!modified}
                        onClick={resetForm}>
                    Undo
                </Button>
                <Button color="primary" size="large"
                        disabled={!valid || !modified}
                        onClick={submitForm}>
                    Save
                </Button>
            </div>
            <Prompt when={modified} message={verbiage.UNSAVED_CHANGES}/>
        </div>
}

export default EditTemplatePage;
